import { useState } from "react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Polygon, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api, formatApiError } from "../lib/api";
import { headlineOf } from "./Suitability";

export const FOREST_BADGE = {
  GREEN: ["Dapat Dilanjutkan", "bg-emerald-100 text-emerald-800 border-emerald-300"],
  YELLOW: ["Perlu Telaah", "bg-amber-100 text-amber-900 border-amber-300"],
  RED: ["Peringatan Kawasan", "bg-red-100 text-red-800 border-red-300"],
  GREY: ["Belum Ada Data", "bg-slate-100 text-slate-600 border-slate-300"],
};
const GROUP_COLOR = { SENSITIVE: "#dc2626", REVIEW: "#f59e0b", OK: "#16a34a" };
const pct = (v) => `${String(v).replace(".", ",")}%`;
const ha = (v) => `${Number(v).toLocaleString("id-ID", { maximumFractionDigits: 2 })} ha`;
const wib = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", dateStyle: "medium", timeStyle: "short" }) + " WIB" : "-");

export const ForestBadge = ({ fs, testid }) => {
  const [label, cls] = FOREST_BADGE[fs?.warningLevel || "GREY"];
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-bold ${cls}`} data-testid={testid}>{label}</span>;
};

const ForestMap = ({ boundary, overlay }) => (
  <div className="h-64 rounded-lg overflow-hidden border" data-testid="forest-map">
    <MapContainer bounds={boundary} className="h-full w-full" scrollWheelZoom={false}>
      <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" attribution="Esri" />
      {(overlay || []).map((o, i) => o.rings.map((r, j) => (
        <Polygon key={`${i}-${j}`} positions={r} pathOptions={{ color: GROUP_COLOR[o.group], weight: 1, fillOpacity: 0.25 }}><Tooltip>{o.short}</Tooltip></Polygon>)))}
      <Polygon positions={boundary} pathOptions={{ color: "#facc15", weight: 3, fillOpacity: 0 }} />
    </MapContainer>
  </div>
);

const B = process.env.REACT_APP_BACKEND_URL;
const kb = (n) => `${Math.max(1, Math.round(n / 1024)).toLocaleString("id-ID")} KB`;

const OfficialFiles = ({ a, reload }) => {
  const [kind, setKind] = useState("SURAT");
  const [busy, setBusy] = useState(false);
  const files = a.forestOfficialFiles || [];
  const base = `/admin/land-applications/${a.id}/forest-official/files`;
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);
    setBusy(true);
    try { await api.post(base, fd); toast.success("File analisis resmi diunggah"); reload(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Hapus file "${f.name}"?`)) return;
    try { await api.delete(`${base}/${f.id}`); toast.success("File dihapus"); reload(); } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="space-y-2 rounded-lg border border-dashed p-3" data-testid="forest-official-files">
      <p className="text-xs font-bold text-slate-600">Surat / peta resmi BPKH</p>
      <ul className="space-y-1 text-xs">
        {files.map((f) => (
          <li key={f.id} className="flex items-center justify-between gap-2" data-testid={`forest-official-file-${f.id}`}>
            <a className="text-emerald-800 underline break-all" href={`${B}/api${base}/${f.id}`} target="_blank" rel="noreferrer" data-testid={`forest-official-file-link-${f.id}`}>
              {f.kind === "PETA" ? "Peta" : "Surat"}: {f.name} <span className="text-slate-500">({kb(f.size)})</span></a>
            <button type="button" className="text-red-700 shrink-0" onClick={() => remove(f)} data-testid={`forest-official-file-delete-${f.id}`}>Hapus</button>
          </li>))}
        {!files.length && <li className="text-slate-500" data-testid="forest-official-files-empty">Belum ada file.</li>}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded-md border px-2 text-xs" data-testid="forest-official-file-kind">
          <option value="SURAT">Surat analisis</option><option value="PETA">Peta resmi</option></select>
        <label className={`inline-flex h-8 cursor-pointer items-center rounded-md border px-3 text-xs font-semibold ${busy ? "opacity-50 pointer-events-none" : "hover:bg-slate-50"}`} data-testid="forest-official-file-upload">
          {busy ? "Mengunggah…" : "Unggah File"}
          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.kml,.kmz,.geojson,.zip" onChange={upload} data-testid="forest-official-file-input" /></label>
      </div>
      <p className="text-[11px] text-slate-500">PDF, JPG/PNG, KML/KMZ, GeoJSON, atau ZIP shapefile · maks 25 MB · hanya terlihat PT SJI.</p>
    </div>
  );
};

const ReviewForms = ({ a, reload }) => {
  const [note, setNote] = useState("");
  const off = a.forestOfficial || {};
  const [o, setO] = useState({ letterNumber: off.letterNumber || "", letterDate: off.letterDate || "", result: off.result || "", note: off.note || "" });
  const save = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="grid md:grid-cols-2 gap-4 border-t pt-4">
      <div className="space-y-2" data-testid="forest-review-form">
        <p className="text-sm font-bold">Hasil telaah Owner {a.forestReview && <span className="font-normal text-xs text-emerald-800">(tercatat {wib(a.forestReview.at)} oleh {a.forestReview.byName})</span>}</p>
        {a.forestReview && <p className="text-xs text-slate-600" data-testid="forest-review-note">{a.forestReview.note}</p>}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan hasil telaah status kawasan (min. 5 karakter)" data-testid="forest-review-input" />
        <Button size="sm" disabled={note.trim().length < 5} onClick={() => save(() => api.put(`/admin/land-applications/${a.id}/forest-review`, { note }), "Hasil telaah dicatat")} data-testid="forest-review-save">Catat Hasil Telaah</Button>
      </div>
      <div className="space-y-2" data-testid="forest-official-form">
        <p className="text-sm font-bold">PERLU ANALISIS RESMI BPKH / KEMENTERIAN KEHUTANAN</p>
        <Input value={o.letterNumber} onChange={(e) => setO({ ...o, letterNumber: e.target.value })} placeholder="Nomor surat analisis" data-testid="forest-official-number" />
        <Input type="date" value={o.letterDate} onChange={(e) => setO({ ...o, letterDate: e.target.value })} data-testid="forest-official-date" />
        <Textarea value={o.result} onChange={(e) => setO({ ...o, result: e.target.value })} placeholder="Hasil analisis resmi" data-testid="forest-official-result" />
        <Input value={o.note} onChange={(e) => setO({ ...o, note: e.target.value })} placeholder="Catatan" data-testid="forest-official-note" />
        <Button size="sm" variant="outline" onClick={() => save(() => api.put(`/admin/land-applications/${a.id}/forest-official`, { required: true, ...o }), "Data analisis resmi disimpan")} data-testid="forest-official-save">Simpan Analisis Resmi</Button>
        <OfficialFiles a={a} reload={reload} />
      </div>
    </div>
  );
};

export const ForestScreeningCard = ({ a, reload }) => {
  const fs = a.forestScreening;
  const [busy, setBusy] = useState(false);
  const recheck = async () => {
    setBusy(true);
    try { await api.post(`/admin/land-applications/${a.id}/forest-screening`); toast.success("Status kawasan dicek ulang"); reload(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const h = a.suitability ? headlineOf(a.suitability) : null;
  const forest = (fs?.overlaps || []).filter((o) => o.group !== "OK");
  const border = { GREEN: "border-emerald-300", YELLOW: "border-amber-300", RED: "border-red-400", GREY: "border-slate-300" }[fs?.warningLevel || "GREY"];
  return (
    <div className={`bg-white border-2 ${border} rounded-xl p-4 sm:p-5 space-y-4`} data-testid="forest-screening-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-extrabold text-slate-900">STATUS LAHAN — Kawasan Hutan</h3>
        <div className="flex items-center gap-2"><ForestBadge fs={fs} testid="forest-badge-detail" />
          <Button size="sm" variant="outline" disabled={busy} onClick={recheck} data-testid="forest-recheck-btn">Cek Ulang Status Kawasan</Button></div>
      </div>
      {!fs && <p className="text-sm text-slate-600" data-testid="forest-pending">Screening sedang diproses otomatis. Muat ulang halaman dalam beberapa detik.</p>}
      {fs && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-lg bg-slate-50 p-3" data-testid="forest-layer-a"><p className="text-xs font-bold text-slate-500">A. STATUS KAWASAN / HUTAN</p>
              {fs.overlaps?.length ? fs.overlaps.map((o) => <p key={o.code + (o.decreeNumber || "")} className="font-semibold">{o.short} — {pct(o.percentage)} <span className="font-normal text-slate-500">({ha(o.areaHa)})</span></p>) : <p>-</p>}</div>
            <div className="rounded-lg bg-slate-50 p-3" data-testid="forest-layer-b"><p className="text-xs font-bold text-slate-500">B. STATUS AWAL KEBUN KOPI</p><p className="font-extrabold">{fs.projectReviewLabel?.replace("PROYEK", "KEBUN")}</p></div>
            <div className="rounded-lg bg-slate-50 p-3" data-testid="forest-suitability"><p className="text-xs font-bold text-slate-500">KESESUAIAN KOPI (agronomi, terpisah)</p>
              <p className="font-semibold">{h ? `${h.species === "ROBUSTA" ? "Robusta" : "Arabika"} ${h.score ?? "-"}/100 — ${h.categoryLabel}` : "-"}</p></div>
          </div>
          <div className={`rounded-lg p-3 text-sm ${forest.length ? (fs.warningLevel === "RED" ? "bg-red-50 text-red-900" : "bg-amber-50 text-amber-900") : "bg-emerald-50 text-emerald-900"}`} data-testid="forest-warning">
            <p className="font-bold">{fs.warningLevel === "RED" ? "PERINGATAN: Lahan terindikasi beririsan dengan kawasan yang memerlukan pemeriksaan lebih lanjut. Jangan dipublikasikan ke Investor, jangan aktifkan booking atau kontrak sebelum ditelaah." : forest.length ? "Pengajuan ini terindikasi beririsan dengan kawasan hutan berdasarkan data geospasial yang digunakan. Perlu telaah lebih lanjut sebelum lahan ditawarkan sebagai Rencana Kebun." : fs.status === "FAILED" ? "Screening kawasan belum berhasil. Lakukan pengecekan ulang." : "Tidak ada peringatan kawasan berdasarkan data screening saat ini. Dapat dilanjutkan ke review kebun kopi."}</p>
            <ul className="list-disc pl-5 mt-1 text-xs">{(fs.reasons || []).map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
          {forest.length > 0 && <table className="w-full text-xs" data-testid="forest-overlap-table"><thead className="text-slate-500 text-left"><tr><th>Fungsi</th><th>Luas</th><th>%</th><th>Kawasan</th><th>No./Tgl SK</th></tr></thead>
            <tbody>{forest.map((o) => <tr key={o.code + (o.decreeNumber || "")} className="border-t"><td className="py-1 font-semibold">{o.functionName} ({o.short})</td><td>{ha(o.areaHa)}</td><td>{pct(o.percentage)}</td><td>{o.forestName || "-"}</td><td>{o.decreeNumber || "-"} {o.decreeDate || ""}</td></tr>)}</tbody></table>}
          {a.boundary?.length >= 3 && fs.status === "DONE" && <ForestMap boundary={a.boundary} overlay={fs.overlay} />}
          <p className="text-[11px] text-slate-500" data-testid="forest-source">Sumber: {fs.datasetName || "-"} · versi {fs.datasetVersion || "-"} · diperiksa {wib(fs.checkedAt)} · {fs.method} {fs.limitations} {fs.note}</p>
          {fs.status === "FAILED" && <p className="text-xs text-red-700" data-testid="forest-error">{fs.error} (dicoba ulang otomatis tiap 15 menit, percobaan ke-{fs.attempts})</p>}
          <ReviewForms a={a} reload={reload} />
        </>
      )}
    </div>
  );
};
